// RCM Dashboard — direction D data model (rcm-01-dashboard.md).
// Billing staff see counts and plain sentences; never percentages, arrows or AI chips.
//
// Every number and row here is READ from the stores of the pages it opens (one
// shared state): Insurance A/R, Denied & underpaid, Pre-auths, Claims, Human in
// the loop, Document Center, Posting and ELVA's work Log. Nothing on this page is
// a hand-typed copy of another page's figure.

import { PMS } from '@/features/shared/pms';
import {
  getAppealSettings,
  getSentSync as getAppealsSentSync,
  getDeniedSync,
  getUnderpaidSync,
  deniedTotalSync,
  underpaidShortSync,
  sendBlockedReason,
  waitingCountSync as appealsWaitingCountSync,
} from '@/features/appeals/store';
import { TODAY_ISO } from '@/features/appeals/utils';
import { getClaimSync, getQueueSync } from '@/features/claims-review/store';
import { needsCountSync as docNeedsCountSync, getDocsSync } from '@/features/document-center/store';
import { getLogEntriesFlat } from '@/features/elva-work/store';
import type { LogEntry, LogKind } from '@/features/elva-work/types';
import { getItemsSync, openCountSync } from '@/features/human-in-the-loop/store';
import type { HitlItem } from '@/features/human-in-the-loop/types';
import {
  getClaimCountSync,
  getLateSync,
  getOntimeSync,
  getOwedSync,
  getPaysPatientSummary,
  getSettingsSync as getArSettingsSync,
  lateCountSync,
  lateTotalSync,
} from '@/features/insurance-ar/store';
import { payerSlug } from '@/features/insurance-ar/payers';
import { expectedPayIso, isoPlusDays, lateReasonSummary } from '@/features/insurance-ar/utils';
import { getPostedForPeriod, getPostedThisMonth, type PostedFigures } from './postedData';

export { getPostedThisMonth };
export type { PostedFigures };
import {
  getApprovedSync,
  getSentSync as getPreauthSentSync,
  getToSendSync,
  isSentDecided as isPreauthSentDecided,
} from '@/features/preauths/store';
import { toPostCountSync } from '@/features/posting/store';

export type RcmDashboardPeriod = 'today' | 'week' | 'month';

export const RCM_PERIOD_OPTIONS: { value: RcmDashboardPeriod; label: string; word: string }[] = [
  { value: 'today', label: 'Today', word: 'today' },
  { value: 'week', label: 'This week', word: 'this week' },
  { value: 'month', label: 'This month', word: 'this month' },
];

export function isDashboardPeriod(v: string | null): v is RcmDashboardPeriod {
  return v === 'today' || v === 'week' || v === 'month';
}

/** The one mock "today" every RCM page shares (Pre-auths, Denied, ELVA's work, HITL). */
export const DASHBOARD_TODAY_ISO = TODAY_ISO;

// ── types ───────────────────────────────────────────────────────────

/** 01 Money coming in — expected insurer payment by payer. */
export interface PayerExpected {
  /** Stable key for React. */
  key: string;
  payer: string;
  /** `/ar?payer={id}`; "Other" opens the whole A/R list. */
  href: string;
  amount: number;
  claims: number;
}

/** Payers paying slower than their usual days-to-pay. Empty when none. */
export interface SlowPayerNote {
  payers: string[];
  days: number[];
  usual: number[];
  href: string;
}

export type NeedsYouSource = 'hitl' | 'claims' | 'preauths' | 'denied' | 'document';
export type NeedsYouGroup = 'now' | 'tomorrow' | 'week' | 'later';

/** 02 Needs you — one item from any page, linking to its OWN url. */
export interface NeedsYouItem {
  /** Unique across sources. */
  key: string;
  source: NeedsYouSource;
  /** "Human in the loop", "Pre-auths" … shown in mono before the sentence. */
  sourceLabel: string;
  href: string;
  title: string;
  context: string;
  group: NeedsYouGroup;
  /** ISO date used to order items inside a group (a "since" date counts as its own date). */
  dueIso: string;
  /** "SINCE OCT 14", "TOMORROW", "BY OCT 20". Never a countdown or a clock time. */
  tag: string;
  /** Set when the tag is a "since" date, for the Needs-a-person figure. */
  sinceIso?: string | undefined;
}

export interface PageCountLink {
  label: string;
  count: number;
  href: string;
}

/** 03 ELVA did this today — one timeline row. Text is segmented so money/refs render correctly. */
export type TimelineSegment =
  | { t: 'text'; value: string }
  | { t: 'money'; value: string } // bold
  | { t: 'ref'; value: string } // mono
  | { t: 'link'; value: string; href: string };

export interface TimelineRow {
  id: string;
  time: string; // 12-hour with AM/PM, no leading zero
  category: string; // "POSTING" | "APPEALS" | "CALL" | "CLAIMS" …
  segments: TimelineSegment[];
}

/** 04 Money at risk — one leader row. */
export interface AtRiskRow {
  id: string;
  label: string;
  amount: number;
  sub: string;
  /** A second line saying why (Late: "68 past the payer's usual time · 4 by your 60-day flag"). */
  meta?: string;
  href: string;
}

/** 01 Money coming in › Overdue: expected pay date already passed. Never added to the 30-day figure. */
export interface OverdueLine {
  amount: number;
  claims: number;
  /** `/ar?group=late` */
  href: string;
}

// ── per-period figures ──────────────────────────────────────────────

interface PeriodData {
  /** Kicker range: "{range} · as of {time} today". */
  kickerRange: string;
  /** Plain-sentence delta vs the same span of the previous period. No %, no colour. Static: the previous period has no rows. */
  postedDelta: string;
  production: number;
}

const PERIOD: Record<RcmDashboardPeriod, PeriodData> = {
  today: {
    kickerRange: 'Oct 15',
    postedDelta: '$540 more than last Thursday',
    production: 7800,
  },
  week: {
    kickerRange: 'Oct 12–15',
    postedDelta: '$2,180 more than Oct 5–8',
    production: 39200,
  },
  month: {
    kickerRange: 'October 1–15',
    postedDelta: '$5,430 more than Sep 1–15',
    production: 87420,
  },
};

/** First ISO date of each period (inclusive) up to DASHBOARD_TODAY_ISO. */
const PERIOD_FROM_ISO: Record<RcmDashboardPeriod, string> = {
  today: '2026-10-15',
  week: '2026-10-12',
  month: '2026-10-01',
};

// GAP: no store exports the "as of" clock yet; it stays local.
const AS_OF_TIME = '10:42 AM';

// ── small date helpers ──────────────────────────────────────────────

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function isoParts(iso: string): { y: number; m: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

function dayNumber(iso: string): number {
  const p = isoParts(iso);
  if (!p) return 0;
  return Math.round(Date.UTC(p.y, p.m - 1, p.d) / 86_400_000);
}

/** "2026-10-14" → "Oct 14". */
function monthDay(iso: string): string {
  const p = isoParts(iso);
  if (!p) return iso;
  return `${MONTHS[p.m - 1] ?? ''} ${String(p.d)}`;
}

/** "Oct 14" / "Oct 6, 2026" / "Dec 29" → ISO (year defaults to the mock year). */
function parseMonthDay(display: string): string | null {
  const m = /([A-Z][a-z]{2}) (\d{1,2})(?:, (\d{4}))?/.exec(display);
  if (!m) return null;
  const idx = MONTHS.indexOf(m[1] ?? '');
  if (idx < 0) return null;
  const year = m[3] ?? DASHBOARD_TODAY_ISO.slice(0, 4);
  return `${year}-${String(idx + 1).padStart(2, '0')}-${String(Number(m[2])).padStart(2, '0')}`;
}

function daysFromToday(iso: string): number {
  return dayNumber(iso) - dayNumber(DASHBOARD_TODAY_ISO);
}

/** Deadline group from a due date, using the Human in the loop rule. */
function groupForDue(iso: string): NeedsYouGroup {
  const d = daysFromToday(iso);
  if (d <= 0) return 'now';
  if (d === 1) return 'tomorrow';
  if (d <= 7) return 'week';
  return 'later';
}

const GROUP_RANK: Record<NeedsYouGroup, number> = { now: 0, tomorrow: 1, week: 2, later: 3 };

function firstSentence(text: string): string {
  const m = /^(.+?[.!?])(\s|$)/.exec(text);
  return (m?.[1] ?? text).trim();
}

function slug(name: string): string {
  // One slug per payer, shared with Insurance A/R's ?payer= filter.
  return payerSlug(name);
}

// ── 01 Money coming in ──────────────────────────────────────────────

/** "about 2 weeks" → 14. Falls back to 30. */
function usualDays(usually: string | undefined): number {
  const m = /(\d+)\s*week/.exec(usually ?? '');
  return m ? Number(m[1]) * 7 : 30;
}

/** ["Aetna", "Delta"] → "Aetna and Delta". */
export function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1] ?? ''}`;
}

/** The payer names H11's table uses. */
function dashboardPayerName(name: string): string {
  if (name === 'Delta Dental of Texas') return 'Delta Dental';
  if (name === 'BlueCross BlueShield of Texas') return 'BCBS of Texas';
  return name;
}

function buildMoneyComingIn(): {
  payers: PayerExpected[];
  total: number;
  slow: SlowPayerNote;
  overdue: OverdueLine;
  noUsualTime: number;
} {
  // Expected pay date = sent date + the payer's usual time, the same as on Insurance A/R (H11 §2).
  //  - between today and +30 days  -> "Expected next 30 days"
  //  - already passed              -> "Overdue" (its own line, never in the 30-day figure)
  //  - more than 30 days away, or no usual pay time yet -> in neither
  // So the two never have to add up to "Owed by insurers".
  const late = getLateSync();
  const claims = [...late, ...getOntimeSync()];
  const windowEnd = isoPlusDays(DASHBOARD_TODAY_ISO, 30);
  const byPayer = new Map<string, { payer: string; name: string; amount: number; claims: number }>();
  let overdueAmount = 0;
  let overdueClaims = 0;
  for (const c of claims) {
    const expected = expectedPayIso(c);
    if (expected === undefined) continue;
    if (expected < DASHBOARD_TODAY_ISO) {
      overdueAmount += c.balance;
      overdueClaims += 1;
    } else if (expected <= windowEnd) {
      const cur = byPayer.get(c.payer.name) ?? { payer: c.payer.name, name: dashboardPayerName(c.payer.name), amount: 0, claims: 0 };
      cur.amount += c.balance;
      cur.claims += 1;
      byPayer.set(c.payer.name, cur);
    }
  }
  const noUsualTime = getOntimeSync().filter((c) => expectedPayIso(c) === undefined).length;
  const sorted = [...byPayer.values()].sort((a, b) => b.amount - a.amount);
  const top = sorted.slice(0, 5);
  const rest = sorted.slice(5);
  const payers: PayerExpected[] = top.map((p) => ({
    key: slug(p.payer),
    payer: p.name,
    href: `/ar?payer=${slug(p.payer)}`,
    amount: p.amount,
    claims: p.claims,
  }));
  if (rest.length) {
    payers.push({
      key: 'other',
      payer: 'Other',
      href: '/ar',
      amount: rest.reduce((s, p) => s + p.amount, 0),
      claims: rest.reduce((s, p) => s + p.claims, 0),
    });
  }
  // The by-payer rows sum to the 30-day figure, not to what's owed.
  const total = payers.reduce((s, p) => s + p.amount, 0);

  // Slow payers: late claims older than the payer's usual days-to-pay.
  const times = getArSettingsSync().payerTimes;
  const lateByPayer = new Map<string, { short: string; days: number[]; usual: number }>();
  for (const c of late) {
    const cur = lateByPayer.get(c.payer.name) ?? {
      short: c.payer.short,
      days: [],
      usual: usualDays(times.find((t) => t.payer === c.payer.name)?.usually),
    };
    cur.days.push(c.days);
    lateByPayer.set(c.payer.name, cur);
  }
  const slowRows = [...lateByPayer.values()]
    .map((r) => ({
      short: r.short,
      avg: Math.round(r.days.reduce((s, d) => s + d, 0) / r.days.length),
      usual: r.usual,
    }))
    .filter((r) => r.avg > r.usual)
    .sort((a, b) => b.avg - b.usual - (a.avg - a.usual))
    .slice(0, 2);
  const slow: SlowPayerNote = {
    payers: slowRows.map((r) => r.short),
    days: slowRows.map((r) => r.avg),
    usual: slowRows.map((r) => r.usual),
    href: `/ar?payer=${slowRows.map((r) => slug(r.short)).join(',')}`,
  };
  return {
    payers,
    total,
    slow,
    overdue: { amount: overdueAmount, claims: overdueClaims, href: '/ar?group=late' },
    noUsualTime,
  };
}

// ── 02 Needs you ────────────────────────────────────────────────────

function hitlTag(item: HitlItem): { tag: string; sinceIso?: string; dueIso: string } {
  const text = item.deadlineText.trim();
  const since = /^since (.+)$/i.exec(text);
  if (since?.[1]) {
    const iso = parseMonthDay(since[1]) ?? DASHBOARD_TODAY_ISO;
    return { tag: `SINCE ${monthDay(iso).toUpperCase()}`, sinceIso: iso, dueIso: iso };
  }
  const posted = /^posted (.+)$/i.exec(text);
  if (posted?.[1]) {
    const iso = parseMonthDay(posted[1]) ?? DASHBOARD_TODAY_ISO;
    return { tag: `SINCE ${monthDay(iso).toUpperCase()}`, sinceIso: iso, dueIso: iso };
  }
  const by = /^by (.+)$/i.exec(text);
  if (by?.[1]) {
    const iso = parseMonthDay(by[1]) ?? DASHBOARD_TODAY_ISO;
    return { tag: `BY ${monthDay(iso).toUpperCase()}`, dueIso: iso };
  }
  if (/today/i.test(text)) {
    return { tag: 'TODAY', sinceIso: DASHBOARD_TODAY_ISO, dueIso: DASHBOARD_TODAY_ISO };
  }
  if (/^tomorrow$/i.test(text)) {
    const t = dayNumber(DASHBOARD_TODAY_ISO) + 1;
    const d = new Date(t * 86_400_000);
    const iso = `${String(d.getUTCFullYear())}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
    return { tag: 'TOMORROW', dueIso: iso };
  }
  if (/follow-up limit/i.test(text)) {
    return { tag: 'AT YOUR LIMIT', dueIso: '9999-12-30' };
  }
  return { tag: text.toUpperCase(), dueIso: '9999-12-30' };
}

function collectNeedsYou(): NeedsYouItem[] {
  const items: NeedsYouItem[] = [];

  // Human in the loop — every open item.
  const hitl = getItemsSync();
  const hitlIds = new Set(hitl.map((i) => i.id));
  for (const i of hitl) {
    const t = hitlTag(i);
    items.push({
      key: `hitl:${i.id}`,
      source: 'hitl',
      sourceLabel: 'Human in the loop',
      href: `/human-in-the-loop/${i.id}`,
      title: i.title,
      context: firstSentence(i.why),
      group: i.group,
      dueIso: t.dueIso,
      tag: t.tag,
      sinceIso: t.sinceIso,
    });
  }

  // Claims › Needs your decision.
  for (const s of getQueueSync()) {
    if (s.tag !== 'needs_you') continue;
    const claim = getClaimSync(s.id);
    const past = daysFromToday(s.serviceDate) <= 0;
    items.push({
      key: `claims:${s.id}`,
      source: 'claims',
      sourceLabel: 'Claims',
      href: `/claims/${s.id}`,
      title: claim?.decision?.title ?? `${s.patient}: ${s.payerShort} claim needs your decision`,
      context: `${s.patient} · ${s.payerShort}. ${s.listLine}.`,
      group: past ? 'week' : 'later',
      dueIso: s.serviceDate,
      tag: past ? `SINCE ${monthDay(s.serviceDate).toUpperCase()}` : 'LATER',
      sinceIso: past ? s.serviceDate : undefined,
    });
  }

  // Pre-auths › Needs you on every tab (same rules as the Pre-auths store).
  for (const t of getToSendSync()) {
    if (t.group !== 'needs_you') continue;
    const first = t.firstName;
    items.push({
      key: `preauths:${t.id}`,
      source: 'preauths',
      sourceLabel: 'Pre-auths',
      href: `/pre-auths/${t.id}`,
      title: `Pre-auth for ${t.patient}`,
      context: `${t.reasonLine}. ${first}’s visit is ${monthDay(t.appointmentDate)}.`,
      group: groupForDue(t.appointmentDate),
      dueIso: t.appointmentDate,
      tag: `BY ${monthDay(t.appointmentDate).toUpperCase()}`,
    });
  }
  for (const s of getPreauthSentSync()) {
    if (s.action !== 'decide' || (s.closed ?? false) || isPreauthSentDecided(s.id)) continue;
    const visit = parseMonthDay(s.appointmentDisplay) ?? '9999-12-30';
    items.push({
      key: `preauths:${s.id}`,
      source: 'preauths',
      sourceLabel: 'Pre-auths',
      href: `/pre-auths/${s.id}`,
      title: `${s.payerShort} denied ${s.patient}’s pre-auth`,
      context: firstSentence(s.sentence),
      group: groupForDue(visit),
      dueIso: visit,
      tag: `BY ${monthDay(visit).toUpperCase()}`,
    });
  }
  for (const a of getApprovedSync()) {
    const flagged = a.state === 'expires_first' || a.state === 'not_booked';
    if (!flagged) continue;
    const due = parseMonthDay(a.validUntil) ?? '9999-12-30';
    items.push({
      key: `preauths:${a.id}`,
      source: 'preauths',
      sourceLabel: 'Pre-auths',
      // GAP: Approved items have no detail route; the Approved tab lists flagged ones first.
      href: '/pre-auths/approved',
      title: `${a.patient}’s approved pre-auth needs a visit`,
      context: firstSentence(a.sentence),
      group: groupForDue(due),
      dueIso: due,
      tag: `BY ${monthDay(due).toUpperCase()}`,
    });
  }

  // Denied & underpaid › Needs you.
  for (const c of [...getDeniedSync(), ...getUnderpaidSync()]) {
    if (c.answer !== 'needs_you') continue;
    const since = parseMonthDay(c.deniedDate ?? c.paidDate ?? '') ?? DASHBOARD_TODAY_ISO;
    items.push({
      key: `denied:${c.id}`,
      source: 'denied',
      sourceLabel: 'Denied & underpaid',
      href: `/denied/${c.id}`,
      title: c.decision?.title ?? `${c.payer.short} ${c.kind === 'denied' ? 'denied' : 'underpaid'} ${c.patient}’s claim`,
      context: `${c.patient} · ${c.payer.short}. ${c.why.headline}.`,
      group: 'week',
      dueIso: since,
      tag: `SINCE ${monthDay(since).toUpperCase()}`,
      sinceIso: since,
    });
  }

  // Document Center › Needs you. A document that is also a "Read a document"
  // Human in the loop item counts once (the HITL row wins).
  for (const d of getDocsSync()) {
    if (d.group !== 'needs' || d.status !== 'needs') continue;
    if (d.hitlItemId && hitlIds.has(d.hitlItemId)) continue;
    items.push({
      key: `document:${d.id}`,
      source: 'document',
      sourceLabel: 'Document Center',
      href: `/document-center/${d.id}`,
      title: d.title,
      context: d.metaLine,
      group: 'now',
      dueIso: DASHBOARD_TODAY_ISO,
      tag: 'TODAY',
      sinceIso: DASHBOARD_TODAY_ISO,
    });
  }

  // De-duplicate by key, then order: group, then date, then source order.
  const seen = new Set<string>();
  const unique = items.filter((i) => {
    if (seen.has(i.key)) return false;
    seen.add(i.key);
    return true;
  });
  return unique
    .map((item, idx) => ({ item, idx }))
    .sort((a, b) => {
      const g = GROUP_RANK[a.item.group] - GROUP_RANK[b.item.group];
      if (g !== 0) return g;
      if (a.item.dueIso !== b.item.dueIso) return a.item.dueIso < b.item.dueIso ? -1 : 1;
      return a.idx - b.idx;
    })
    .map((x) => x.item);
}

/** "1 waiting since yesterday" / "since Oct 12" / "next one due tomorrow" / "Nothing waiting". */
function needsFigureSub(all: NeedsYouItem[]): string {
  if (all.length === 0) return 'Nothing waiting';
  const today = all.filter((i) => i.group === 'now');
  if (today.length) {
    const since = today
      .map((i) => i.sinceIso ?? DASHBOARD_TODAY_ISO)
      .sort((a, b) => (a < b ? -1 : 1))[0] ?? DASHBOARD_TODAY_ISO;
    const n = today.filter((i) => (i.sinceIso ?? DASHBOARD_TODAY_ISO) <= since).length;
    const age = daysFromToday(since);
    let when = 'today';
    if (age === -1) when = 'yesterday';
    else if (age < -1) when = monthDay(since);
    return `${String(n)} waiting since ${when}`;
  }
  const next = all[0];
  if (!next) return 'Nothing waiting';
  const d = daysFromToday(next.dueIso);
  if (d === 1) return 'Next one due tomorrow';
  if (next.dueIso < '9999') return `Next one due ${monthDay(next.dueIso)}`;
  return 'Nothing due today';
}

function buildPageCounts(): { needs: PageCountLink[]; waiting: PageCountLink[] } {
  const queue = getQueueSync();
  const claimsNeed = queue.filter((s) => s.tag === 'needs_you').length;
  const claimsReady = queue.filter((s) => s.group === 'ready').length;

  const preNeed =
    getToSendSync().filter((t) => t.group === 'needs_you').length +
    getPreauthSentSync().filter((s) => s.action === 'decide' && !(s.closed ?? false) && !isPreauthSentDecided(s.id)).length +
    getApprovedSync().filter((a) => a.state === 'expires_first' || a.state === 'not_booked').length;
  const preReady = getToSendSync().filter((t) => t.group === 'ready').length;

  const deniedNeed = [...getDeniedSync(), ...getUnderpaidSync()].filter((c) => c.answer === 'needs_you').length;
  const appealsWaiting = Math.max(appealsWaitingCountSync() - deniedNeed, 0);

  const needs: PageCountLink[] = [
    { label: 'Human in the loop', count: openCountSync(), href: '/human-in-the-loop' },
    { label: 'Claims', count: claimsNeed, href: '/claims' },
    { label: 'Pre-auths', count: preNeed, href: '/pre-auths' },
    { label: 'Denied & underpaid', count: deniedNeed, href: '/denied' },
    { label: 'Document Center', count: docNeedsCountSync(), href: '/document-center' },
  ].filter((p) => p.count > 0);

  const waiting: PageCountLink[] = [
    { label: 'Claims', count: claimsReady, href: '/claims' },
    { label: 'Posting', count: toPostCountSync(), href: '/posting' },
    { label: 'Pre-auths', count: preReady, href: '/pre-auths' },
    { label: 'Appeals', count: appealsWaiting, href: '/denied' },
  ].filter((p) => p.count > 0);

  return { needs, waiting };
}

// ── 03 ELVA did this today (read from ELVA's work › Log) ────────────

const CATEGORY: Record<LogKind, string> = {
  posted: 'POSTING',
  appeal: 'APPEALS',
  call: 'CALL',
  claims: 'CLAIMS',
  document: 'DOCUMENT',
  portal: 'PORTAL',
  your_team: 'YOUR TEAM',
};

function minutesOf(time: string): number {
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(time.trim());
  if (!m) return 0;
  let h = Number(m[1]) % 12;
  if ((m[3] ?? '').toUpperCase() === 'PM') h += 12;
  return h * 60 + Number(m[2]);
}

function clock(time: string): string {
  return time.trim().replace(/^0/, '');
}

const MONEY_RE = /\$\d[\d,]*(?:\.\d{2})?/g;

function withMoney(text: string): TimelineSegment[] {
  const out: TimelineSegment[] = [];
  let last = 0;
  for (const m of text.matchAll(MONEY_RE)) {
    const at = m.index;
    if (at > last) out.push({ t: 'text', value: text.slice(last, at) });
    out.push({ t: 'money', value: m[0] });
    last = at + m[0].length;
  }
  if (last < text.length) out.push({ t: 'text', value: text.slice(last) });
  return out;
}

function lowerFirst(s: string): string {
  return /^[A-Z][a-z]/.test(s) || /^[A-Z]$/.test(s) ? s.charAt(0).toLowerCase() + s.slice(1) : s;
}

function entrySegments(e: LogEntry): TimelineSegment[] {
  const segs: TimelineSegment[] = [];
  const posted = e.kind === 'posted' ? /^Posted (\$\d[\d,]*(?:\.\d{2})?) on (.+)$/.exec(e.what) : null;
  if (posted?.[1] && posted[2]) {
    // A posting is checked by read-back in the PMS — never "accepted by the clearinghouse".
    segs.push({ t: 'text', value: `${posted[2]}: ` });
    segs.push({ t: 'money', value: posted[1] });
    segs.push({ t: 'text', value: ` posted and checked in ${PMS}.` });
    return segs;
  }
  segs.push(...withMoney(e.what));
  const [lead, ...refs] = e.result.split(' · ');
  const outcome = e.kind === 'posted' ? `posted and checked in ${PMS}` : lowerFirst(lead ?? e.result);
  segs.push({ t: 'text', value: ` — ${outcome}` });
  if (refs.length) {
    segs.push({ t: 'text', value: ' · ' });
    segs.push({ t: 'ref', value: refs.join(' · ') });
    segs.push({ t: 'text', value: '. ' });
  } else {
    segs.push({ t: 'text', value: '. ' });
  }
  if (e.kind === 'call' && e.durationSec !== undefined) {
    segs.push({ t: 'link', value: 'Hear the call', href: `/elva-work/log/${e.id}` });
  }
  return segs;
}

function buildTimeline(period: RcmDashboardPeriod): TimelineRow[] {
  const from = PERIOD_FROM_ISO[period];
  return getLogEntriesFlat()
    // The generated history rows carry a different "today"; the curated entries
    // are the ones that share this mock day.
    .filter((e) => !e.id.startsWith('logh-'))
    .filter((e) => e.kind !== 'your_team' && !(e.resultBad ?? false))
    .filter((e) => (e.dateIso ?? '') >= from && (e.dateIso ?? '') <= DASHBOARD_TODAY_ISO)
    .sort((a, b) => {
      const da = a.dateIso ?? '';
      const db = b.dateIso ?? '';
      if (da !== db) return da < db ? 1 : -1;
      return minutesOf(b.time) - minutesOf(a.time);
    })
    .slice(0, 6)
    .map((e) => ({
      id: e.id,
      time: clock(e.time),
      category: CATEGORY[e.kind],
      segments: entrySegments(e),
    }));
}

// ── 04 Money at risk (four disjoint rows, one shared state) ─────────

function buildAtRisk(): AtRiskRow[] {
  const late = getLateSync();
  const atLimit = late.filter((c) => c.status.kind === 'limit').length;
  const lateCount = lateCountSync();
  // Why they're late (D311): each claim's own reason, the same words as Insurance A/R's rows.
  const lateWhy = lateReasonSummary(late).text;

  const denied = getDeniedSync();
  const appealSettings = getAppealSettings();
  const ready = denied.filter((c) => c.answer === 'appeal' && sendBlockedReason(c, appealSettings) === null).length;

  const underpaid = getUnderpaidSync();
  const sentBack = getAppealsSentSync().filter((r) => r.status === 'reprocessing' && !(r.closed ?? false)).length;

  const flagged = getApprovedSync().filter(
    (a) => a.state === 'expires_first' || a.state === 'not_booked',
  );

  const parts = (n: string, extra: string | null): string => (extra ? `${n} · ${extra}` : n);

  return [
    {
      id: 'risk-late',
      label: 'Late',
      amount: lateTotalSync(),
      sub: parts(
        pluralize(lateCount, 'claim'),
        atLimit ? `${String(atLimit)} at your follow-up limit ${atLimit === 1 ? 'needs' : 'need'} a decision` : null,
      ),
      ...(lateWhy !== '' ? { meta: lateWhy } : {}),
      href: '/ar?group=late',
    },
    {
      id: 'risk-denied',
      label: 'Denied',
      amount: deniedTotalSync(),
      sub: parts(pluralize(denied.length, 'claim'), ready ? `${pluralize(ready, 'appeal')} ready to send` : null),
      href: '/denied',
    },
    {
      id: 'risk-short',
      label: 'Paid short',
      amount: underpaidShortSync(),
      sub: parts(pluralize(underpaid.length, 'payment'), sentBack ? `${String(sentBack)} sent back to the payer` : null),
      href: '/denied/underpaid',
    },
    {
      id: 'risk-preauth',
      label: 'Approved pre-auths at risk',
      amount: flagged.reduce((s, a) => s + a.estimate, 0),
      sub: `${pluralize(flagged.length, 'pre-auth')} · payer’s estimate`,
      href: '/pre-auths/approved',
    },
  ];
}

// ── snapshot ────────────────────────────────────────────────────────

export interface DashboardSnapshot {
  period: RcmDashboardPeriod;
  periodWord: string;
  kicker: string;
  // headline + figures
  /** Insurance payments posted in the PMS this period, by anyone, net of reversals and undos. Never "paid". */
  posted: number;
  /** "of which ELVA posted" */
  elvaPosted: number;
  /** `?scenario=returned-check`: posted payments proven not to have arrived. 0 otherwise. The posted figure is unchanged. */
  confirmedNotReceived: number;
  postedDelta: string;
  production: number;
  pmsName: string;
  owed: number;
  owedClaims: number;
  /** Claims where the payer pays the patient: followed, but not in "Owed by insurers" (D197). */
  notCounted: { count: number; href: string };
  needsCount: number;
  needsSub: string;
  // sections
  payersExpected: PayerExpected[];
  payersTotal: number;
  /** Overdue: expected pay date passed. Its own line, not in payersTotal. */
  overdue: OverdueLine;
  /** Claims whose payer has no usual pay time yet: in neither the 30-day figure nor Overdue. */
  noUsualTimeClaims: number;
  slowPayer: SlowPayerNote;
  needsYou: NeedsYouItem[];
  needsPageCounts: PageCountLink[];
  waitingForOk: PageCountLink[];
  timeline: TimelineRow[];
  atRisk: AtRiskRow[];
}

export function getDashboardData(period: RcmDashboardPeriod): DashboardSnapshot {
  const p = PERIOD[period];
  const word = RCM_PERIOD_OPTIONS.find((o) => o.value === period)?.word ?? 'this month';
  const money = buildMoneyComingIn();
  const posted = getPostedForPeriod(period);
  const needsAll = collectNeedsYou();
  const counts = buildPageCounts();

  return {
    period,
    periodWord: word,
    kicker: `${p.kickerRange} · as of ${AS_OF_TIME} today`,
    posted: posted.total,
    elvaPosted: posted.elvaPosted,
    confirmedNotReceived: posted.confirmedNotReceived,
    postedDelta: p.postedDelta,
    production: p.production,
    pmsName: PMS,
    // The Insurance A/R headline, read from the same state ("$38,412.60 on 64 claims").
    owed: getOwedSync(),
    owedClaims: getClaimCountSync(),
    notCounted: (() => {
      const { count, href } = getPaysPatientSummary();
      return { count, href };
    })(),
    needsCount: needsAll.length,
    needsSub: needsFigureSub(needsAll),
    payersExpected: money.payers,
    payersTotal: money.total,
    overdue: money.overdue,
    noUsualTimeClaims: money.noUsualTime,
    slowPayer: money.slow,
    needsYou: needsAll.slice(0, 4),
    needsPageCounts: counts.needs,
    waitingForOk: counts.waiting,
    timeline: buildTimeline(period),
    atRisk: buildAtRisk(),
  };
}

// ── formatting helpers ──────────────────────────────────────────────

export function formatMoney(amount: number): string {
  return `$${Math.round(amount).toLocaleString('en-US')}`;
}

/** "$5,000.00": two decimals, for a figure that is an exact amount. */
export function formatMoneyCents(amount: number): string {
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Round to a readable "about" figure for the 01 meta: the nearest $100. */
export function formatAbout(amount: number): string {
  const rounded = Math.round(amount / 100) * 100;
  return `$${rounded.toLocaleString('en-US')}`;
}

const COUNT_WORDS = [
  'Zero',
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
  'Seven',
  'Eight',
  'Nine',
  'Ten',
];

/** Count spelled as a word up to ten, else the numeral. */
export function spellCount(n: number): string {
  return n >= 0 && n <= 10 ? (COUNT_WORDS[n] ?? String(n)) : String(n);
}

/** "One thing needs" / "Five things need". */
export function needThingsClause(n: number): string {
  if (n === 0) return 'Nothing needs a person.';
  const word = spellCount(n);
  return n === 1 ? `${word} thing needs a person.` : `${word} things need a person.`;
}

/** "1 claim" vs "14 claims". */
export function pluralize(n: number, singular: string, plural?: string): string {
  return n === 1 ? `${String(n)} ${singular}` : `${String(n)} ${plural ?? `${singular}s`}`;
}
